import React from 'react';

const Header = ({ currentUser, onTestAPI, onToggleSidebar, editingResult, editingComponent, editingProperty }) => {
  const hasLoadedData = editingResult || editingComponent || editingProperty ||
    localStorage.getItem('blendOptimizerLoadedEditData') ||
    localStorage.getItem('blendOptimizerLoadedComponents') ||
    localStorage.getItem('blendOptimizerLoadedProperties');

  const handleNewModel = () => {
    const keysToRemove = [
      'blendOptimizerLoadedEditData',
      'blendOptimizerLoadedComponents',
      'blendOptimizerLoadedProperties',
      'blendOptimizerEditData',
      'componentsToLoad',
      'propertiesToLoad',
      'customNotesToLoad',
      'componentsToEdit',
      'propertiesToEdit'
    ];
    keysToRemove.forEach(key => localStorage.removeItem(key));
    window.location.reload();
  };

  return (
    <header className="bg-white border-b border-gray-200 shadow-sm">
      <div className="flex items-center justify-between px-4 sm:px-6 py-3">
        {/* Left: Hamburger + Branding */}
        <div className="flex items-center gap-3 min-w-0">
          <button
            onClick={onToggleSidebar}
            className="lg:hidden p-1.5 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors flex-shrink-0"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>

          {/* Logo mark */}
          <div className="flex items-center gap-2.5 flex-shrink-0">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center shadow-sm">
              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
              </svg>
            </div>
            <div className="hidden sm:block">
              <h1 className="text-base font-bold text-gray-900 leading-tight">
                Blend Optimizer
              </h1>
              <p className="text-xs text-gray-400 leading-tight">ProcessOptimizer.ai</p>
            </div>
            <h1 className="sm:hidden text-base font-bold text-gray-900">BlendOpt</h1>
          </div>

          {/* Context badges */}
          <div className="flex items-center gap-2 ml-1 min-w-0">
            {editingResult && (
              <span className="hidden sm:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200 truncate max-w-[200px]">
                <svg className="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                <span className="truncate">Editing: {editingResult.customName || editingResult.customTextField}</span>
              </span>
            )}
            {editingComponent && (
              <span className="hidden sm:inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                {editingComponent.customName}
              </span>
            )}
            {editingProperty && (
              <span className="hidden sm:inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium bg-purple-50 text-purple-700 border border-purple-200">
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                {editingProperty.customName}
              </span>
            )}
            {hasLoadedData && (
              <button
                onClick={handleNewModel}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 transition-colors rounded-md shadow-sm"
                title="Start a new model (clears current data)"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                <span className="hidden sm:inline">New Model</span>
              </button>
            )}
          </div>
        </div>

        {/* Right: Actions + User */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={onTestAPI}
            className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 hover:text-gray-800 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
            Test API
          </button>

          {/* User pill */}
          <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-2.5 py-1.5">
            <div className="w-6 h-6 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center text-white font-bold text-xs flex-shrink-0">
              {currentUser.username.charAt(0).toUpperCase()}
            </div>
            <div className="hidden xs:block">
              <div className="text-xs font-semibold text-gray-800 leading-tight truncate max-w-[80px] sm:max-w-[120px]">
                {currentUser.username}
              </div>
              <div className="text-xs text-gray-400 capitalize leading-tight">{currentUser.role}</div>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
